import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { ClassType, PeriodType, RunExportStatus } from './../../api';
import useSession from '../Util/useSession';
import { LineupEntry, timeSpanToSeconds } from '../../services/sessionModels';
import sessionsVisualizeService from '../../services/sessionsVisualize';
import {
  useLocationsLazyQuery,
  LocationsQuery,
  GqTeamSide,
  GqTeamType,
  Track,
  GqTrackType,
  useMatchLazyQuery,
  MatchQuery,
} from './../../api-anansi';
import { TSession } from './Sessions';
import { makeStyles, Tooltip, IconButton } from '@material-ui/core';
import SessionTimeline from './Timeline/SessionTimeline';
import VideoLayout from '../VideoPlayer/VideoLayout';
import VideoControls from '../VideoPlayer/VideoControls';
import { Coordinate, Video, VideoOverlayObject } from '../VideoPlayer/VideoTypes';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import Radar from './Radar';
import GoBackButton from './Label/Buttons/GoBackButton';
import { projection } from '../Util/calib';
import PlayerMarker from '../VideoOverlayObjects/PlayerMarker';
import BallMarker from '../VideoOverlayObjects/BallMarker';
import MatchInfoButton from './MatchInfoButton';

export type Diff<T, U> = T extends U ? never : T;
export type TLocation = Diff<Diff<LocationsQuery['match'], null | undefined>['locations'], null | undefined>[0];

const useStyles = makeStyles(() => ({
  flexContainer: {
    display: 'flex',
  },
  sidebar: {
    position: 'relative',
    width: '320px',
    minWidth: '320px',
    height: '60vh',
    overflowY: 'scroll',
  },
  sidebarRow: {
    padding: '8px 0px',
  },
  videoContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80%',
    height: '60vh',
  },
  videoControlsContainer: {
    marginTop: '12px',
    width: '320px',
    minWidth: '320px',
    height: '12vh',
    minHeight: '100px',
    backgroundColor: '#ccf1',
    border: '1px dashed #000',
  },
  timelineContainer: {
    marginTop: '12px',
    width: '80%',
    height: '12vh',
  },
  toggleIcon: {
    color: '#444',
    backgroundColor: '#f5f6fa',
    borderRadius: '24px',
    margin: '0px 4px',
    border: '3px dashed #f5f6fa',
    '&:hover': {
      color: '#ff5a0f',
      backgroundColor: '#fff',
    },
    '&:disabled': {
      backgroundColor: '#e0e0e099',
      color: '#d1af9f',
      border: '3px solid #d1af9f',
    },
    width: '42px',
    height: '42px',
  },
}));

export type VisualizeObject = {
  type: ClassType | undefined;
  lineupEntry: LineupEntry | undefined;
  position: Coordinate;
  video_coordinate?: Map<number, Coordinate>;
  ground_video_coordinate?: Map<number, Coordinate>;
};
export type FrameData = {
  frameId: number;
  objects: Array<VisualizeObject>;
};
type FrameBuffer = {
  firstFrameId: number;
  lastFrameId: number;
  hasProjectedPositions: boolean;
  frameData: Array<FrameData>;
};
type ProjectedData = {
  bufferIndex: number;
  video: Video;
  coords: Coordinate[];
};

const getClassType = (track: Track) => {
  if (track.type === GqTrackType.Ball) {
    return ClassType.Ball;
  } else if (track.teamType === GqTeamType.Referee) {
    return ClassType.Referee;
  } else if (track.team === GqTeamSide.Home) {
    if (track.teamType === GqTeamType.Player) {
      return ClassType.HomePlayer;
    } else if (track.teamType === GqTeamType.Keeper) {
      return ClassType.HomeKeeper;
    }
  } else if (track.team === GqTeamSide.Away) {
    if (track.teamType === GqTeamType.Player) {
      return ClassType.AwayPlayer;
    } else if (track.teamType === GqTeamType.Keeper) {
      return ClassType.AwayKeeper;
    }
  }
  return undefined;
};

export const getClassColor = (session: TSession, classType: ClassType | null | undefined) => {
  if (classType === ClassType.HomeKeeper && session?.homeKeeperColor) {
    return session?.homeKeeperColor;
  } else if (classType === ClassType.HomePlayer && session?.homePlayerColor) {
    return session?.homePlayerColor;
  } else if (classType === ClassType.AwayKeeper && session?.awayKeeperColor) {
    return session?.awayKeeperColor;
  } else if (classType === ClassType.AwayPlayer && session?.awayPlayerColor) {
    return session?.awayPlayerColor;
  } else if (classType === ClassType.Referee && session?.refereeColor) {
    return session?.refereeColor;
  } else if (classType === ClassType.Ball) {
    return '#00eeff';
  }
  //if we didnt find the class, or color isnt set..
  return '#F0F';
};

const getLineupEntry = (classType: ClassType, number: number, lineup: LineupEntry[]) => {
  return lineup.find((check: LineupEntry) => check.type === classType && check.shirtNumber === number);
};

interface Props extends RouteComponentProps<TParams> {}

// Update videoplayer every ms
const updateIntervalMs: number = 40;
// Fetch positions size
const fetchPositionBufferSize: number = 250;
const fetchFrameBuffersSize: number = 6;
// Graphics
const displayScale: number = 0.75;

type TParams = {
  session: string;
};
const SessionVisualize = (props: Props) => {
  const classes = useStyles();
  const { session } = useSession(props.match.params.session);
  const runId: string | null | undefined = session?.lastRun?.id;
  const fps: number =
    (session && session.videos && session?.videos.length > 0 && session?.videos[0].fps) || 25;

  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);
  const [playTime, setPlayTime] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [seekVideo, setSeekVideo] = useState<boolean>(false);
  const [timeStep, setTimeStep] = useState<number>(5);
  const [currentFrameId, setCurrentFrameId] = useState<number>(0);
  const [videoDuration, setVideoDuration] = useState<number>(0);

  const hasSaved: boolean = session?.lastRun?.exportStatus === RunExportStatus.Exporting
    || session?.lastRun?.exportStatus === RunExportStatus.Exported;
  const [autonomousData, setAutonomousData] = useState<boolean>(!hasSaved);
  const [prevAutonomousData, setPrevAutonomousData] = useState<boolean>(!hasSaved);
  const [frameBuffers, setFrameBuffers] = useState<FrameBuffer[]>([]);
  const [lastBufferFrameId, setLastBufferFrameId] = useState<number>(0);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [minLocationsFrameId, setMinLocationsFrameId] = useState<number | undefined>(undefined);
  const [maxLocationsFrameId, setMaxLocationsFrameId] = useState<number | undefined>(undefined);
  const [incomingBufferFrameId, setIncomingBufferFrameId] = useState<number | undefined>(undefined);
  const [projectionQueue, setProjectionQueue] = useState<ProjectedData[]>([]);

  const [playerFrameCountAutonomous, setPlayerFrameCountAutonomous] = useState<number | undefined>(undefined);
  const [playerFrameCountLabeled, setPlayerFrameCountLabeled] = useState<number | undefined>(undefined);
  const [ballFrameCountAutonomous, setBallFrameCountAutonomous] = useState<number | undefined>(undefined);
  const [ballFrameCountLabeled, setBallFrameCountLabeled] = useState<number | undefined>(undefined);
  const [totalPlayerFrameCount, setTotalPlayerFrameCount] = useState<number | undefined>(undefined);
  const [totalBallFrameCount, setTotalBallFrameCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if(session) {
      setAutonomousData(!(session.lastRun?.exportStatus === RunExportStatus.Exporting || session.lastRun?.exportStatus === RunExportStatus.Exported));
    }
  }, [session]);

  const cleanBuffers = useCallback(() => {
    setProjectionQueue([]);
    setLastBufferFrameId(0);
    setFrameBuffers([]);
  }, []);

  const onChangeVideoDuration = useCallback((duration: number) => {
    setVideoDuration(duration);
  }, []);

  const onLoadMatchError = useCallback((error: any) => {
    console.log('Error fetching match from Anansi:', error);
  }, []);

  const onLoadMatch = useCallback((data: MatchQuery) => {
    console.log('Loaded match from Anansi:', data);
    setPlayerFrameCountAutonomous(data.match?.autonomousPlayerFrameCount || undefined);
    setPlayerFrameCountLabeled(data.match?.labeledPlayerFrameCount || undefined);
    setBallFrameCountAutonomous(data.match?.autonomousBallFrameCount || undefined);
    setBallFrameCountLabeled(data.match?.labeledBallFrameCount || undefined);
  }, []);

  const onLoadPositionsError = useCallback((error: any) => {
    setIsLoadingData(false);
    console.log('Error fetching match from Anansi:', error);
  }, []);

  const onLoadPositions = useCallback(
    (data: LocationsQuery) => {
      // Return if data object is invalid
      if ((!data.match?.locationFirstFrame && data.match?.locationFirstFrame !== 0) || !data.match?.locationLastFrame || !data.match?.locations) {
        console.log('Buffer Skipped - Incomplete track buffer data', data);
        return;
      }

      if (!incomingBufferFrameId) {
        console.log('Buffer Skipped - Not expecting any buffer data', data);
        setIsLoadingData(false);
        return;
      }

      // Set min and max when not initialized yet
      if (!minLocationsFrameId) {
        setMinLocationsFrameId(data.match.locationFirstFrame);
      }
      if (!maxLocationsFrameId) {
        setMaxLocationsFrameId(data.match.locationLastFrame);
      }

      if (data.match.locations.length === 0) {
        console.log('Buffer Skipped - Empty track buffer data', data);
        setIncomingBufferFrameId(undefined);
        return;
      }
      if (incomingBufferFrameId < data.match.locationFirstFrame) {
        console.log('Buffer Skipped - Not at first data frame yet.', data);
        setIncomingBufferFrameId(undefined);
        setIsLoadingData(false);
        return;
      }
      if (incomingBufferFrameId > data.match.locationLastFrame) {
        console.log('Buffer Skipped - Beyond last data frame.', data);
        setIncomingBufferFrameId(undefined);
        setIsLoadingData(false);
        return;
      }

      const firstFrame: number = data.match.locations[0]?.frameId || -1;
      const lastFrame: number = data.match.locations[data.match.locations.length - 1]?.frameId || -1;

      if (incomingBufferFrameId !== firstFrame) {
        console.log(
          'Buffer Skipped - first frame not what we expect. Expected: ',
          incomingBufferFrameId,
          ' Given:',
          firstFrame
        );
        setIsLoadingData(false);
        return;
      }
      
      // Create a map where all location objects are grouped to frame id
      const frameLocations: Map<number, TLocation[]> = new Map<number, TLocation[]>();
      data.match.locations.forEach((loc: TLocation) => {
        if(!loc?.frameId) return;
        const exisLocs: TLocation[] | undefined = frameLocations.get(loc.frameId);
        if(exisLocs) {
          frameLocations.set(loc.frameId, [...exisLocs, loc]);
        } else {
          frameLocations.set(loc.frameId, [loc]);
        }
      });

      // Create our frames
      const frames: FrameData[] = [];
      // Loop from start frame id to end..
      for (let frameId: number = firstFrame; frameId < lastFrame; frameId++) {
        const locations = frameLocations.get(frameId);
        if(!locations) {
          continue;
        }

        const frameObjects: VisualizeObject[] = [];
        locations.forEach((positionObject) => {
          if (!positionObject?.track) return;

          const classType: ClassType | undefined = getClassType(positionObject.track);
          const lineupEntry: LineupEntry | undefined =
            classType && positionObject.track.number && session?.lineup
              ? getLineupEntry(classType, positionObject.track.number, session?.lineup)
              : undefined;

          const position: Coordinate = {
            x: positionObject.x || 0,
            y: positionObject.y || 0,
            z: positionObject.z || 0,
          };

          const object: VisualizeObject = {
            type: classType,
            lineupEntry: lineupEntry,
            position: position,
          };

          frameObjects.push(object);
        });

        const frameData: FrameData = {
          frameId: frameId,
          objects: frameObjects,
        };

        frames.push(frameData);
      }

      // Create our track buffer
      const trackBuffer: FrameBuffer = {
        firstFrameId: firstFrame,
        lastFrameId: lastFrame,
        frameData: frames,
        hasProjectedPositions: false,
      };

      // Add buffer and set our last buffer frame id
      setFrameBuffers((prevBuffers) => [...prevBuffers, trackBuffer]);
      setLastBufferFrameId(trackBuffer.lastFrameId);
      setIncomingBufferFrameId(undefined);
      setIsLoadingData(false);
    },
    [incomingBufferFrameId, maxLocationsFrameId, minLocationsFrameId, session]
  );

  const [loadPositionData] = useLocationsLazyQuery({
    fetchPolicy: 'no-cache',
    context: { serviceName: 'anansi' },
    onError: onLoadPositionsError,
    onCompleted: onLoadPositions,
  });

  const [loadMatchData] = useMatchLazyQuery({
    fetchPolicy: 'no-cache',
    context: { serviceName: 'anansi' },
    onError: onLoadMatchError,
    onCompleted: onLoadMatch,
  });

  const loadFrameBuffer = useCallback(
    (frameId: number, requestAutonomous: boolean) => {
      if (runId) {
        if (!session) {
          console.log('Skipping load request. Session:', session, 'Playtime:', playTime);
          return;
        }

        setIsLoadingData(true);
        setIncomingBufferFrameId(frameId);

        loadPositionData({
          variables: {
            matchId: runId,
            fromFrame: frameId,
            toFrame: frameId + fetchPositionBufferSize,
            autonomous: requestAutonomous,
            sort: true,
          },
        });
      }
    },
    [loadPositionData, playTime, runId, session]
  );

  const setVideoPlayingState = useCallback((newPlayingState: boolean) => {
    setVideoPlaying((prevState) => {
      if (prevState === newPlayingState) return prevState;
      return newPlayingState;
    });
  }, []);

  const frameBetweenBuffers = useCallback(
    (frameId: number) => {
      if (frameBuffers.length === 0) return false;
      const firstBuffer: FrameBuffer = frameBuffers[0];
      const lastBuffer: FrameBuffer = frameBuffers[frameBuffers.length - 1];
      return frameId > firstBuffer.firstFrameId && frameId < lastBuffer.lastFrameId;
    },
    [frameBuffers]
  );

  const onSeek = useCallback((time: number) => {
    setSeekVideo(true);
    const frameId: number = Math.round(time * fps);
    if (!frameBetweenBuffers(frameId)) {
      cleanBuffers();
      loadFrameBuffer(frameId, autonomousData);
    }
  }, [autonomousData, cleanBuffers, fps, frameBetweenBuffers, loadFrameBuffer]);

  const absoluteSeek = useCallback(
    (newTime: number) => {
      setPlayTime(prev => {
        // Clamp time to allowed values
        const clampedTime = Math.max(0, Math.min(newTime, videoDuration));
        onSeek(clampedTime);
        return clampedTime;
      });
    },
    [onSeek, videoDuration]
  );

  const relativeSeek = useCallback((timeDiff: number) => {
    setPlayTime(prev => {
      // Clamp time to allowed values
      const clampedTime = Math.max(0, Math.min(prev+timeDiff, videoDuration));
      onSeek(clampedTime);
      return clampedTime;
    });
  }, [videoDuration, onSeek])

  const onToggleAutonomousData = useCallback(() => {
    setAutonomousData((prev) => !prev);
  }, []);

  const projectFrameBufferPositions = useCallback(
    async (bufferIndex: number) => {
      if (frameBuffers.length <= bufferIndex) return;
      const buffer: FrameBuffer = frameBuffers[bufferIndex];

      // Get all our world positions
      const worldPositions: number[][] = [];
      buffer.frameData.forEach((frame: FrameData) => {
        frame.objects.forEach((obj: VisualizeObject) => {
          const pos: number[] = [obj.position.x, obj.position.y, obj.position.z || 0];
          worldPositions.push(pos);
          // Set video coordinate to an empty map
          obj.video_coordinate = new Map<number, Coordinate>();
          if (pos[2] > 0.1) {
            worldPositions.push([pos[0], pos[1], 0]);
            obj.ground_video_coordinate = new Map<number, Coordinate>();
          }
        });
      });

      session?.videos?.forEach((video: Video) => {
        // Get our video calibration
        const calib = JSON.parse(video?.calibration || '');

        const projectedData = projection(calib, worldPositions);

        // Create coords list
        const projCoords: Coordinate[] = [];
        for (let i: number = 0; i < projectedData.length; ++i) {
          const coord = projectedData[i];
          projCoords.push({
            x: coord[0],
            y: coord[1],
          });
        }
        // Set projected positions to handle in effect
        const newData: ProjectedData = {
          bufferIndex: bufferIndex,
          video: video,
          coords: projCoords,
        };
        setProjectionQueue((prev) => [...prev, newData]);
      });
    },
    [frameBuffers, session]
  );

  // Load match data from Anansi
  useEffect(() => {
    if (runId) {
      loadMatchData({
        variables: {
          runId: runId,
        },
      });
    }
  }, [loadMatchData, runId]);

  // On change autonomous data, reload
  useEffect(() => {
    if(prevAutonomousData !== autonomousData) {
      cleanBuffers();
      loadFrameBuffer(currentFrameId, autonomousData);
      setPrevAutonomousData(autonomousData);
    }
  }, [autonomousData, cleanBuffers, currentFrameId, loadFrameBuffer, prevAutonomousData]);

  // Set expected totals
  useEffect(() => {
    let cancelled = false;
    if (session) {
      sessionsVisualizeService
        .getLineupFrameCount(session.id, session.lineup)
        .then((count) => !cancelled && setTotalPlayerFrameCount(count))
        .catch(() => !cancelled && setTotalPlayerFrameCount(undefined));
    }

    let totalBallFrameCount: number = 0;
    session?.labelTasks?.forEach(
      (x) => (totalBallFrameCount += x.classType === ClassType.Ball ? x.endFrame - x.startFrame + 1 : 0)
    );
    setTotalBallFrameCount(totalBallFrameCount);
    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    if (projectionQueue.length > 0) {
      const projectionData: ProjectedData = projectionQueue[0];
      // Handle projection data in frame buffer.
      setFrameBuffers((current: FrameBuffer[]) => {
        if (!current || current.length === 0 || current.length <= projectionData.bufferIndex) {
          console.log('Projection done, but no(t enough) frame buffers', current, projectionData);
          return current;
        }
        // Get our buffer
        const updateBuffer: FrameBuffer = current[projectionData.bufferIndex];

        // Update video coordinates on our buffer
        updateBuffer.frameData.forEach((frame: FrameData) => {
          frame.objects.forEach((obj: VisualizeObject) => {
            if (!obj.video_coordinate) return;
            const coord: Coordinate | undefined = projectionData.coords.shift();
            if (coord) {
              obj.video_coordinate.set(projectionData.video.id, coord);
              if (obj.ground_video_coordinate) {
                const groundCoord: Coordinate | undefined = projectionData.coords.shift();
                if (groundCoord) {
                  obj.ground_video_coordinate.set(projectionData.video.id, groundCoord);
                }
              }
            }
          });
        });

        updateBuffer.hasProjectedPositions = true;

        return [...current];
      });
      // We handled our projection data, so reset it.
      setProjectionQueue((prev) => prev.splice(1));
    }
  }, [projectionQueue]);

  // Go to 3 seconds before kickoff on initialize
  useEffect(() => {
    const newTime: number = timeSpanToSeconds(session?.periods?.find((p) => p.type === PeriodType.FirstHalf)?.startTime) - (timeSpanToSeconds(session?.startAndFinishOffset) || 3);
    const startMatchTime: number = Math.max(0, newTime);
    setPlayTime(startMatchTime);
  }, [session]);

  // Update frame id based on playtime
  useEffect(() => {
    if (playTime) {
      setCurrentFrameId(Math.round(playTime * fps));
    }
  }, [playTime, fps]);

  // Whenever we receive a new frame buffer, trigger projection for video coords
  useEffect(() => {
    // Only continue if we have atleast one buffer
    if (frameBuffers.length === 0) return;

    // Go through buffers, project
    for (let i: number = 0; i < frameBuffers.length; ++i) {
      // Check if positions have already been projected.. if so continue.
      if (frameBuffers[i].hasProjectedPositions) continue;
      // Start projecting..
      projectFrameBufferPositions(i);
    }
  }, [frameBuffers, projectFrameBufferPositions]);

  // Load future frame buffers
  useEffect(() => {
    const bufferSize: number = frameBuffers.length;

    if (isLoadingData || bufferSize >= fetchFrameBuffersSize) {
      return;
    }
    if (maxLocationsFrameId && currentFrameId > maxLocationsFrameId) {
      console.log('Current Frame is higher than maxLocationsFrameId');
      return;
    }
    if (minLocationsFrameId && currentFrameId < minLocationsFrameId) {
      console.log('Current Frame is lower than minLocationsFrameId');
      return;
    }

    if (bufferSize === 0) {
      // Load first buffer
      loadFrameBuffer(currentFrameId, autonomousData);
    } else {
      const prevBuffer: FrameBuffer = frameBuffers[bufferSize - 1];
      const prevBufferIsFull: boolean = prevBuffer.frameData.length === fetchPositionBufferSize;
      if (prevBufferIsFull && frameBetweenBuffers(currentFrameId)) {
        // Load next buffer
        loadFrameBuffer(prevBuffer.lastFrameId, autonomousData);
      }
    }
  }, [
    autonomousData,
    currentFrameId,
    frameBetweenBuffers,
    frameBuffers,
    isLoadingData,
    loadFrameBuffer,
    maxLocationsFrameId,
    minLocationsFrameId,
  ]);

  // Handle frame buffers
  useEffect(() => {
    if (frameBuffers.length === 0) return;

    const firstBuffer: FrameBuffer = frameBuffers[0];
    const lastBuffer: FrameBuffer = frameBuffers[frameBuffers.length - 1];
    if (currentFrameId > lastBuffer.lastFrameId) {
      // Remove all buffers if we're after our last buffer's end
      //console.log("AFTER BUFFERS -> Frame id > lastBuffer.lastFrameId");
      cleanBuffers();
    } else if (currentFrameId < firstBuffer.firstFrameId) {
      // Remove all buffers if we're before our first buffer's start
      //console.log("BEFORE BUFFERS -> Frame id < firstbuffer.firstFrameid");
      cleanBuffers();
    } else if (currentFrameId > firstBuffer.lastFrameId) {
      //console.log("SHIFT -> Frame id > firstBuffer.lastFrameId");
      // Remove first buffer when we're surpassing our first buffer's end
      setFrameBuffers((prev: FrameBuffer[]) => {
        prev.shift();
        return [...prev];
      });
    }
  }, [cleanBuffers, currentFrameId, frameBuffers]);

  const playerCompleteness = useMemo(() => {
    if (totalPlayerFrameCount) {
      if (autonomousData && playerFrameCountAutonomous !== undefined) {
        return ((playerFrameCountAutonomous / totalPlayerFrameCount) * 100).toFixed(2) + '%';
      } else if (!autonomousData && playerFrameCountLabeled !== undefined) {
        return ((playerFrameCountLabeled / totalPlayerFrameCount) * 100).toFixed(2) + '%';
      }
    }
    return '-';
  }, [autonomousData, playerFrameCountAutonomous, playerFrameCountLabeled, totalPlayerFrameCount]);

  const ballCompleteness = useMemo(() => {
    if (totalBallFrameCount) {
      if (autonomousData && ballFrameCountAutonomous !== undefined) {
        return ((ballFrameCountAutonomous / totalBallFrameCount) * 100).toFixed(2) + '%';
      } else if (!autonomousData && ballFrameCountLabeled !== undefined) {
        return ((ballFrameCountLabeled / totalBallFrameCount) * 100).toFixed(2) + '%';
      }
    }
    return '-';
  }, [autonomousData, ballFrameCountAutonomous, ballFrameCountLabeled, totalBallFrameCount]);

  /*
    CREATE VIDEO OVERLAY ITEMS
  */
  const overlayItems = new Map<string, VideoOverlayObject[]>();
  const bufferAvailable: boolean = frameBuffers.length > 0 && frameBuffers[0].hasProjectedPositions;
  const frameIndex: number = bufferAvailable ? currentFrameId - frameBuffers[0].firstFrameId : 0;
  const currentFrame: FrameData | undefined = bufferAvailable ? frameBuffers[0].frameData[frameIndex] : undefined;
  // Current marker graphics
  if (currentFrame) {
    session?.videos?.forEach((video: Video) => {
      const viewOverlayObjects: VideoOverlayObject[] = overlayItems.get(String(video.id)) || [];

      currentFrame.objects.forEach((obj: VisualizeObject) => {
        if (!obj.video_coordinate) return;
        const coord: Coordinate | undefined = obj.video_coordinate.get(video.id);
        if (!coord) return;

        const shirtNumber: number | undefined = obj.lineupEntry?.shirtNumber;
        if (shirtNumber && !autonomousData) {
          // Display shirt number for revised data
          viewOverlayObjects.push({
            svg: (
              <svg>
                <circle cx={coord.x} cy={coord.y + 17} r={10} fill="#0009" />
                <svg x={coord.x - 10} y={coord.y + 12} viewBox="0 0 20 20" fill="#f008" width={20} height={20}>
                  <text x={10} y={10} fill="#fff" textLength={16} textAnchor="middle">
                    {shirtNumber}
                  </text>
                </svg>
              </svg>
            ),
            width: 0,
            height: 0,
            center: coord,
            disableAutoScale: true,
          });
        }

        const teamColor: string = getClassColor(session, obj.type);
        const groundPositionCoord: Coordinate | undefined = obj.ground_video_coordinate ? obj.ground_video_coordinate.get(video.id) : undefined;

        const overlayObjects: VideoOverlayObject[] = obj.type === ClassType.Ball
          ? BallMarker({
            coord: coord,
            color: teamColor,
            inAir: coord.z ? coord.z > 0.05 : false,
            groundCoord: groundPositionCoord,
            centerViewOnThis: true,
            scale: displayScale,
          })
          : PlayerMarker({
              coord: coord,
              centerViewOnThis: false,
              color: teamColor,
              scale: displayScale,
              //video: video,
              //position: obj.position,
            });
          
        viewOverlayObjects.push(...overlayObjects);
      });

      overlayItems.set(String(video.id), viewOverlayObjects);
    });
  }
  /* 
    END VIDEO OVERLAY ITEMS 
  */

  const matchInfoButton = useMemo(() => {
    return <MatchInfoButton matchInfoUrl={session?.matchInfoUrl} />;
  }, [session]);
  
  return (
    <Container className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5">
        <div className={classes.flexContainer}>
          <div className={classes.sidebar}>
            <div className={classes.sidebarRow}>
              <GoBackButton path="/sessions" />
              <Tooltip title={'Toggle Data Mode Display'} arrow>
                <IconButton onClick={onToggleAutonomousData} className={classes.toggleIcon} size="small">
                  <SwapHorizIcon />
                </IconButton>
              </Tooltip>
              { matchInfoButton }
            </div>
            <div className={classes.sidebarRow}>
              <i>
                {isLoadingData &&
                  (frameBuffers.length === 0 ? 'Loading initial data...' : 'Loading additional buffers...')}
              </i>
            </div>
            <div className={classes.sidebarRow}>
              Data mode: <b>{autonomousData ? 'Autonomous' : 'Labeled'}</b>
              <br />
              Frame: <b>{currentFrameId}</b>
            </div>
            <div className={classes.sidebarRow}>
              <b>Completeness</b>
              <br />
              Player: <b>{playerCompleteness}</b>
              <br />
              Ball: <b>{ballCompleteness}</b>
            </div>
            <div style={{ position: 'absolute', bottom: 0 }}>
              <Radar session={session || undefined} currentFrame={currentFrame} />
            </div>
          </div>

          <div className={classes.videoContainer}>
            <VideoLayout
              videos={session?.videos || []}
              showOverlayUI={true}
              updateIntervalMs={updateIntervalMs}
              videoPlaying={videoPlaying}
              onChangeVideoPlaying={setVideoPlayingState}
              playTime={playTime}
              playTimeChanged={setPlayTime}
              seekVideo={seekVideo}
              setSeekVideo={setSeekVideo}
              playbackSpeed={playbackSpeed}
              viewOverlays={overlayItems}
              onChangeVideoDuration={onChangeVideoDuration}
            />
          </div>
        </div>
        <div className={classes.flexContainer}>
          <div className={classes.videoControlsContainer}>
            <VideoControls
              isPlaying={videoPlaying}
              onChangeVideoPlaying={setVideoPlayingState}
              relativeSeek={relativeSeek}
              playbackSpeed={playbackSpeed}
              setPlaybackSpeed={setPlaybackSpeed}
              timeStep={timeStep}
              setTimeStep={setTimeStep}
            />
          </div>
          <div className={classes.timelineContainer}>
            <SessionTimeline
              playTime={playTime}
              fps={fps}
              seekToPlayTime={absoluteSeek}
              videoDuration={videoDuration}
              teamHome={session?.homeTeam}
              teamAway={session?.awayTeam}
              periods={session?.periods || []}
              events={session?.events || []}
              lastProgressionTime={lastBufferFrameId / fps}
              slidingWindow
            />
          </div>
        </div>
    </Container>
  );
};

const Container = styled.div`
  min-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
`;

export default SessionVisualize;
